import { Array, Number, Record, String, pipe } from 'effect';
import { Option, isSome } from 'effect/Option';

export type ModAlgorithm = 'DBLAL' | 'MOD10' | 'MOD11';

export interface SortCodeAccount {
  sortCode: string;
  accountNumber: string;
}

export type Weight = {
  u: number;
  v: number;
  w: number;
  x: number;
  y: number;
  z: number;
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  g: number;
  h: number;
};

/**
 * A weight table item describes which validation algorithm to
 * use on a specific range of sort codes.
 */
export interface WeightTableItem {
  algorithm: ModAlgorithm;
  exception: Option<string>;
  sortCodeRange: { start: string; end: string };
  weight: Weight;
}

export type SortCodeWeightTable = ReadonlyArray<WeightTableItem>;

/**
 * Returns the Sort Code + Accountnumber as digits-array
 */
export function getAccount(accountNumber: string, sortCode: string): number[] {
  return pipe(
    String.concat(sortCode, accountNumber),
    String.split(''),
    Array.map(Number.parse),
    Array.getSomes,
  );
}

export const hasException: (weightTableItem: WeightTableItem) => boolean = (
  weightTableItem: WeightTableItem,
) => pipe(Record.get(weightTableItem, 'exception'), isSome);

export const positions: Record<string, number> = {
  u: 0,
  v: 1,
  w: 2,
  x: 3,
  y: 4,
  z: 5,
  a: 6,
  b: 7,
  c: 8,
  d: 9,
  e: 10,
  f: 11,
  g: 12,
  h: 13,
};

export const zeroise = {
  u: 0,
  v: 0,
  w: 0,
  x: 0,
  y: 0,
  z: 0,
  a: 0,
  b: 0,
  c: 0,
  d: 0,
  e: 0,
  f: 0,
  g: 0,
  h: 0,
};

export const zeroise_u_b: (weight: Weight) => Weight = (weight: Weight) => ({
  u: 0,
  v: 0,
  w: 0,
  x: 0,
  y: 0,
  z: 0,
  a: 0,
  b: 0,
  c: weight.c,
  d: weight.d,
  e: weight.e,
  f: weight.f,
  g: weight.g,
  h: weight.h,
});

/**
 * Parameters used by the CheckFn
 */
export interface CheckFnParameters {
  /** Digits of Sort Code + Account Number as Number Array */
  account: number[];
  /** Digits for the weight as Number Array */
  weight: number[];
  /** Functions registered to override check-handling */
  check?: ModulusCheckOverrides;
}

export interface ApplyAccountNumberSubstitutionsOptions {
  accountNumber: string;
  checkNumber?: number;
}

export interface ApplySortCodeSubstitutionsOptions {
  sortCode: string;
  checkNumber?: number;
}

export interface ReplaceWeightOptions {
  account: number[];
  weight: Weight;
  checkNumber?: number;
  weightTable?: SortCodeWeightTable;
}

export interface OverrideTestOptions {
  sum: number;
  account: number[];
  algorithm: ModAlgorithm;
  checkNumber?: number;
}

/**
 * Functions to pass to the Modulus Check in order to
 * override parameters & the check itself for exception handling.
 */
export interface ModulusCheckOverrides {
  /**
   * Replace Weight
   *
   * Can be used to use a different weight than the one
   * from the WeightTable, or apply zeroised weight, in order
   * to skip a check.
   */
  replaceWeight: (opts: ReplaceWeightOptions) => Weight;
  /**
   * Substitutions for Account Number
   *
   * Can be used to replace the account number for the modulus checking
   */
  applyAccountNumberSubstitutions: (
    opts: ApplyAccountNumberSubstitutionsOptions,
  ) => string;
  /**
   * Substitutions for Sort Code
   *
   * Can be used to replace the sortcode for the modulus checking.
   */
  applySortCodeSubstitutions: (
    opts: ApplySortCodeSubstitutionsOptions,
  ) => string;
  /**
   * Function to override the check for the modulus-algorithm.
   *
   * Can be used in order to have custom tests, using check-digits
   * for some specific sort codes.
   */
  overrideTest?: (opts: OverrideTestOptions) => boolean;
}
